/**
 * AddToCart — click delegation for `[data-action=add-to-cart]` buttons.
 * Performs `POST /cart/add.js`, dispatches `product:added` on success,
 * shows inline error UI on failure.
 *
 * Extracted from global.js. Used by the cart drawer, product cards, sticky
 * add-to-cart, and collection/search results.
 *
 * Loaded as classic defer script from `layout/theme.liquid` AFTER
 * `delegate.js` (needs `Delegate`) and BEFORE `global.js` (so existing
 * `new AddToCart(...)` bare-name calls in global.js classes resolve).
 * Attaches `window.AddToCart` for feature modules.
 *
 * Depends on:
 *   - Delegate (delegate.js)
 *   - Form (still in global.js — resolves at method-call time)
 *   - window.routes (theme.liquid inline)
 *   - window.theme (theme.liquid inline)
 *
 * Phase 3 migration will convert to `export class AddToCart` + importmap.
 */

import { Delegate } from '@theme/delegate';
import { Form } from '@theme/form';
import { syncHeaderCartCount } from '@theme/cart-visible-count';

function getBundleInfo(item) {
  const properties = item?.properties;
  if (!properties || typeof properties !== 'object') {
    return { role: '', bundleId: '' };
  }

  return {
    role: String(properties._bundle_role ?? '').toLowerCase(),
    bundleId: String(properties._bundle_id ?? ''),
  };
}

export function buildCartQuantityUpdate(cart, lineId, quantity = 0) {
  const items = Array.isArray(cart?.items) ? cart.items : [];
  const line = items.find((item) => String(item?.key) === String(lineId)) || null;
  const { role, bundleId } = getBundleInfo(line);

  if (role === 'parent' && bundleId) {
    const updates = {};

    for (const item of items) {
      const key = String(item?.key || '');
      if (!key) continue;

      const info = getBundleInfo(item);
      if (key === String(lineId) || (info.role === 'addon' && info.bundleId === bundleId)) {
        updates[key] = quantity;
      }
    }

    return { endpoint: '/cart/update.js', body: { updates } };
  }

  return {
    endpoint: '/cart/change.js',
    body: { id: lineId, quantity },
  };
}

export class AddToCart {
  static getCartErrorMessage(content = {}) {
    return content?.description || window.cartStrings?.error || 'Failed to update cart';
  }

  static showStatusToast(message, target) {
    if (!message || !target) return;
    const errorMessageElement = document.createElement('span');
    errorMessageElement.className = 'u-p4 accent';
    errorMessageElement.textContent = message;
    target.insertAdjacentElement('afterend', errorMessageElement);
    window.setTimeout(() => errorMessageElement.remove(), 2500);
  }

  constructor(element) {
    this.element = element;
    this.delegateElement = new Delegate(this.element);
    this.pageLoader = document.body.querySelector('.page-loader');
    this.bindEvents();
  }

  bindEvents() {
    this.delegateElement.on('click', '[data-action=add-to-cart]', this.addToCart.bind(this));
  }

  addToCart(event) {
    var _this = this;
    event.preventDefault();
    var addToCartButton = event.target;
    this.pageLoader.style.width = 100 + '%';
    addToCartButton.setAttribute('disabled', 'disabled');
    var formElement = event.target.closest('form');
    formElement.querySelector('[name="id"]').removeAttribute('disabled');
    var formHelper = new Form();
    const serialized = formHelper.serialize(formElement);
    fetch(window.routes.localeRootUrl + '/cart/add.js', {
      body: JSON.stringify(serialized),
      credentials: 'same-origin',
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
    }).then(async function (response) {
      if (response.ok) {
        if (window.theme.cartMode === 'page') {
          location.href = window.routes.cart_page_url;
        }
        if (window.location.href.indexOf('cart') != -1) {
          location.reload();
          return;
        }
        addToCartButton.removeAttribute('disabled');
        const addedDetail = {
          variant: serialized.id,
          quantity: serialized.quantity,
        };
        _this.element.dispatchEvent(
          new CustomEvent('product:added', {
            bubbles: true,
            detail: addedDetail,
          })
        );
        document.dispatchEvent(
          new CustomEvent('product:added', { detail: addedDetail })
        );
        await syncHeaderCartCount();
      } else {
        response.json().then(function (content) {
          if (_this.element.querySelector('.ProductForm--Error')) {
            _this.element.querySelector('.ProductForm--Error').classList.add('visible');
            setTimeout(function () {
              _this.element.querySelector('.ProductForm--Error').classList.remove('visible');
            }, 2500);
            _this.pageLoader.style.left = 'unset';
            _this.pageLoader.style.right = 0 + 'px';
            _this.pageLoader.style.width = 0 + '%';
            setTimeout(() => {
              _this.pageLoader.style.left = 0 + 'px';
              _this.pageLoader.style.right = 'unset';
            }, 550);
          } else {
            var errorMessageElement = document.createElement('span');
            errorMessageElement.className = 'u-p4 accent';
            errorMessageElement.innerHTML = content['description'];
            addToCartButton.removeAttribute('disabled');
            addToCartButton.insertAdjacentElement('afterend', errorMessageElement);
            setTimeout(function () {
              errorMessageElement.remove();
            }, 2500);
            _this.pageLoader.style.left = 'unset';
            _this.pageLoader.style.right = 0 + 'px';
            _this.pageLoader.style.width = 0 + '%';
            setTimeout(() => {
              _this.pageLoader.style.left = 0 + 'px';
              _this.pageLoader.style.right = 'unset';
            }, 550);
          }
        });
      }
    });
  }
}

// Bridge for classic-script consumers (global.js). Removed in Faza 5.
window.AddToCart = AddToCart;
